import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogIn, LogOut, User, Users, BookOpen, Menu, X, LayoutDashboard, PenLine, MessageSquare } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useUnreadCount } from '../hooks/useUnreadCount';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuthStore();
  const isTestPage = location.pathname === '/test';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const isExpert = user?.role === 'expert';
  const isAdmin = user?.role === 'admin';
  const unreadCount = useUnreadCount();

  const navLinkClass = (active = false) =>
    `flex items-center gap-1.5 text-sm font-medium transition-colors ${
      active
        ? 'text-indigo-600'
        : 'text-slate-600 hover:text-indigo-600'
    }`;

  return (
    <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img
                src="/uploads/media/enneagram-free-logo.svg"
                alt="Enneagram Free"
                className="h-8 sm:h-9"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          {!isTestPage && (
            <div className="hidden sm:flex items-center gap-6">
              <Link to="/experts" className={navLinkClass(location.pathname.startsWith('/experts'))}>
                <Users className="h-4 w-4" />
                <span>Experts</span>
              </Link>

              {isAuthenticated ? (
                <>
                  {isAdmin ? (
                    <Link to="/admin" className={navLinkClass(location.pathname.startsWith('/admin'))}>
                      <LayoutDashboard className="h-4 w-4" />
                      <span>Admin</span>
                    </Link>
                  ) : isExpert ? (
                    <Link to="/expert/dashboard" className={navLinkClass(location.pathname.startsWith('/expert'))}>
                      <LayoutDashboard className="h-4 w-4" />
                      <span>Dashboard</span>
                    </Link>
                  ) : (
                    <>
                      <Link to="/dashboard" className={navLinkClass(location.pathname === '/dashboard')}>
                        <User className="h-4 w-4" />
                        <span>Dashboard</span>
                      </Link>
                      <Link to="/for-contributors" className={navLinkClass(location.pathname === '/for-contributors')}>
                        <PenLine className="h-4 w-4" />
                        <span>Contribute</span>
                      </Link>
                    </>
                  )}

                  <Link to="/learning" className={navLinkClass(location.pathname.startsWith('/learning'))}>
                    <BookOpen className="h-4 w-4" />
                    <span>Learning</span>
                  </Link>

                  <Link to="/inbox" className={`relative ${navLinkClass(location.pathname === '/inbox')}`}>
                    <MessageSquare className="h-4 w-4" />
                    <span>Inbox</span>
                    {unreadCount > 0 && (
                      <span className="absolute -top-1.5 -right-2.5 bg-red-500 text-white text-[10px] font-bold rounded-full px-1 min-w-[1rem] text-center leading-tight">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </Link>

                  <button
                    onClick={handleLogout}
                    className={navLinkClass()}
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <>
                  <Link to="/for-contributors" className={navLinkClass(location.pathname === '/for-contributors')}>
                    <PenLine className="h-4 w-4" />
                    <span>Contribute</span>
                  </Link>
                  <Link
                    to="/login"
                    className="text-sm font-semibold text-slate-700 hover:text-indigo-600 transition-colors"
                  >
                    Log in
                  </Link>
                  <Link
                    to="/test"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold shadow-primary hover:bg-indigo-700 transition-all hover:-translate-y-px"
                  >
                    <BookOpen className="h-4 w-4" />
                    Get started
                  </Link>
                </>
              )}
            </div>
          )}

          {/* Mobile right side */}
          <div className="flex items-center gap-2">
            {isTestPage && (
              <Link
                to="/experts"
                className="inline-flex items-center gap-1 text-xs sm:text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 px-3 py-2 rounded-xl transition-colors shadow-primary"
              >
                <Users className="h-3.5 w-3.5" />
                <span className="hidden xs:inline">Find Expert</span>
              </Link>
            )}

            {!isTestPage && (
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="sm:hidden inline-flex items-center justify-center p-2 rounded-lg text-slate-600 hover:text-indigo-600 hover:bg-slate-100 transition-colors"
                aria-label="Toggle mobile menu"
              >
                {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {!isTestPage && isMobileMenuOpen && (
          <div className="sm:hidden absolute top-16 left-0 right-0 bg-white/95 backdrop-blur-md shadow-lg border-b border-slate-200 z-50">
            <div className="px-4 py-5 space-y-1">
              <Link
                to="/experts"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
              >
                <Users className="h-5 w-5 text-slate-400" />
                <span className="font-medium">Find Experts</span>
              </Link>

              <Link
                to="/learning"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
              >
                <BookOpen className="h-5 w-5 text-slate-400" />
                <span className="font-medium">Learning</span>
              </Link>

              <div className="border-t border-slate-100 pt-3 mt-3 space-y-1">
                {isAuthenticated ? (
                  <>
                    {isAdmin ? (
                      <Link to="/admin" onClick={closeMobileMenu} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors">
                        <LayoutDashboard className="h-5 w-5 text-slate-400" />
                        <span className="font-medium">Admin Dashboard</span>
                      </Link>
                    ) : isExpert ? (
                      <Link to="/expert/dashboard" onClick={closeMobileMenu} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors">
                        <LayoutDashboard className="h-5 w-5 text-slate-400" />
                        <span className="font-medium">Contributor Dashboard</span>
                      </Link>
                    ) : (
                      <>
                        <Link to="/dashboard" onClick={closeMobileMenu} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors">
                          <User className="h-5 w-5 text-slate-400" />
                          <span className="font-medium">Dashboard</span>
                        </Link>
                        <Link to="/for-contributors" onClick={closeMobileMenu} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-indigo-600 hover:bg-indigo-50 transition-colors">
                          <PenLine className="h-5 w-5" />
                          <span className="font-medium">Contribute</span>
                        </Link>
                      </>
                    )}
                    <Link to="/inbox" onClick={closeMobileMenu} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors">
                      <MessageSquare className="h-5 w-5 text-slate-400" />
                      <span className="font-medium">Inbox</span>
                      {unreadCount > 0 && (
                        <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </Link>
                    <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-700 hover:bg-slate-50 transition-colors">
                      <LogOut className="h-5 w-5 text-slate-400" />
                      <span className="font-medium">Logout</span>
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/for-contributors" onClick={closeMobileMenu} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-indigo-600 hover:bg-indigo-50 transition-colors">
                      <PenLine className="h-5 w-5" />
                      <span className="font-medium">Contribute</span>
                    </Link>
                    <Link to="/login" onClick={closeMobileMenu} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-700 hover:bg-slate-50 transition-colors">
                      <LogIn className="h-5 w-5 text-slate-400" />
                      <span className="font-medium">Log in</span>
                    </Link>
                    <Link to="/test" onClick={closeMobileMenu} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition-colors">
                      <BookOpen className="h-5 w-5" />
                      <span className="font-medium">Get started</span>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
